import {
  ArrowBackOutlined,
  CheckCircleOutlined,
  CancelOutlined,
  OpenInNew,
  InsertDriveFile,
  ExpandMore,
} from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { Tag } from 'finara-components';
import { useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';

import { StepDataSummary } from '@/components/admin-onboarding/StepDataSummary';
import { ErrorState, LoadingState } from '@/components/common';
import { Navbar } from '@/components/super-admin/general';
import { superAdminNavItems } from '@/data/super-admin/general/navigationData';
import {
  useApplicationDetail,
  useApproveApplication,
  useRejectApplication,
} from '@/hooks/queries/useAdminOnboarding';
import type { ReviewStatus } from '@/types/adminOnboarding';

const STATUS_VARIANTS: Record<ReviewStatus, 'gray' | 'yellow' | 'green' | 'red'> = {
  in_progress: 'gray',
  pending_review: 'yellow',
  approved: 'green',
  rejected: 'red',
};

const STATUS_LABELS: Record<ReviewStatus, string> = {
  in_progress: 'En progreso',
  pending_review: 'Pendiente revisión',
  approved: 'Aprobada',
  rejected: 'Rechazada',
};

const STEP_TITLES: Record<number, string> = {
  1: 'Identidad Legal',
  2: 'Información de Contacto',
  3: 'Detalles del Negocio',
  4: 'Identificadores Corporativos',
  5: 'Documentos Legales',
  6: 'Beneficiarios Finales',
};

const UboDocList: React.FC<{ docs: import('@/types/adminOnboarding').FunnelDocument[] }> = ({
  docs,
}) => {
  if (docs.length === 0) return <p className="text-xs text-muted">Sin documentos.</p>;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
      {docs.map(doc => (
        <div key={doc.id} className="flex items-center gap-3 p-3 border border-border rounded-lg">
          <InsertDriveFile className="text-muted flex-shrink-0" fontSize="small" />
          <div className="flex-1 min-w-0">
            <p className="text-sm text-brand-white truncate">{doc.originalFilename}</p>
            <p className="text-xs text-muted">{doc.documentType}</p>
          </div>
          {doc.signedUrl && (
            <a
              href={doc.signedUrl}
              target="_blank"
              rel="noreferrer"
              className="p-1.5 text-muted hover:text-blue-600 transition-colors flex-shrink-0"
              title="Ver documento"
            >
              <OpenInNew fontSize="small" />
            </a>
          )}
        </div>
      ))}
    </div>
  );
};

export const ApplicationDetailPage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();

  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectStepOrder, setRejectStepOrder] = useState(1);
  const [rejectMessage, setRejectMessage] = useState('');

  const { data: app, isLoading, isError, refetch } = useApplicationDetail(userId);
  const approveMutation = useApproveApplication();
  const rejectMutation = useRejectApplication();

  const handleApprove = async () => {
    try {
      await approveMutation.mutateAsync(userId!);
      toast.success('Solicitud aprobada exitosamente.');
      setApproveOpen(false);
      void navigate('/clientes/onboarding-review');
    } catch (err) {
      toast.error((err as Error).message ?? 'Error al aprobar la solicitud.');
    }
  };

  const handleReject = async () => {
    if (!rejectMessage.trim()) {
      toast.error('Ingresa un mensaje de rechazo.');
      return;
    }
    try {
      await rejectMutation.mutateAsync({
        userId: userId!,
        data: { stepOrder: rejectStepOrder, message: rejectMessage },
      });
      toast.success('Solicitud rechazada. El administrador será notificado.');
      setRejectOpen(false);
      void refetch();
    } catch (err) {
      toast.error((err as Error).message ?? 'Error al rechazar la solicitud.');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen">
        <Navbar navItems={superAdminNavItems} activeItem="Clientes" />
        <LoadingState message="Cargando solicitud..." />
      </div>
    );
  }

  if (isError || !app) {
    return (
      <div className="min-h-screen">
        <Navbar navItems={superAdminNavItems} activeItem="Clientes" />
        <ErrorState title="Error al cargar la solicitud" showRetry onRetry={() => void refetch()} />
      </div>
    );
  }

  const canReview = app.reviewStatus === 'pending_review';
  const stepDocs = app.documents ?? [];

  return (
    <div className="min-h-screen">
      <Navbar navItems={superAdminNavItems} activeItem="Clientes" />

      <div className="pt-6 max-w-4xl mx-auto px-4">
        {/* Back + Header */}
        <div className="mb-6">
          <button
            onClick={() => void navigate('/clientes/onboarding-review')}
            className="flex items-center gap-1.5 text-sm text-muted hover:text-brand-white mb-4 transition-colors"
          >
            <ArrowBackOutlined fontSize="small" />
            Volver al listado
          </button>

          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div>
              <h1 className="text-xl font-bold text-brand-white">{app.companyName ?? app.userName}</h1>
              <p className="text-sm text-muted mt-0.5">{app.email}</p>
              {app.submittedAt && (
                <p className="text-xs text-muted mt-1">
                  Enviado:{' '}
                  {new Date(app.submittedAt).toLocaleDateString('es-MX', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </p>
              )}
            </div>
            <div className="flex flex-col items-start sm:items-end gap-2">
              <Tag variant={STATUS_VARIANTS[app.reviewStatus]}>
                {STATUS_LABELS[app.reviewStatus]}
              </Tag>
              {canReview && (
                <div className="flex gap-2">
                  <button
                    onClick={() => setRejectOpen(true)}
                    className="flex items-center gap-1.5 px-4 py-2 text-sm text-red-400 border border-red-500/30 rounded-lg hover:bg-red-500/10 transition-colors"
                  >
                    <CancelOutlined fontSize="small" />
                    Rechazar
                  </button>
                  <button
                    onClick={() => setApproveOpen(true)}
                    className="flex items-center gap-1.5 px-4 py-2 text-sm text-white bg-green-600/80 rounded-lg hover:bg-green-600 transition-colors"
                  >
                    <CheckCircleOutlined fontSize="small" />
                    Aprobar
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Step Data Accordion */}
        <div className="mb-6">
          <h2 className="text-base font-semibold text-brand-white mb-3">Información enviada</h2>
          <StepDataSummary stepData={app.steps} />
        </div>

        {/* Documents by step */}
        {stepDocs.length > 0 && (
          <div className="mb-6">
            <h2 className="text-base font-semibold text-brand-white mb-3">Documentos</h2>
            {[1, 5].map(stepOrder => {
              const docs = stepDocs.filter(d => d.stepOrder === stepOrder);
              if (docs.length === 0) return null;
              return (
                <Accordion
                  key={stepOrder}
                  disableGutters
                  elevation={0}
                  sx={{
                    backgroundColor: 'var(--color-surface)',
                    border: '1px solid var(--color-border)',
                    borderRadius: '8px !important',
                    mb: 1,
                    '&:before': { display: 'none' },
                    '& .MuiAccordionSummary-expandIconWrapper': { color: 'var(--color-brand-white)' },
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <span className="font-medium text-brand-white text-sm">
                      Paso {stepOrder}: {STEP_TITLES[stepOrder]}
                    </span>
                  </AccordionSummary>
                  <AccordionDetails>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {docs.map(doc => (
                        <div
                          key={doc.id}
                          className="flex items-center gap-3 p-3 border border-border rounded-lg"
                        >
                          <InsertDriveFile
                            className="text-muted flex-shrink-0"
                            fontSize="small"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-brand-white truncate">{doc.originalFilename}</p>
                            <p className="text-xs text-muted">{doc.documentType}</p>
                          </div>
                          {doc.signedUrl && (
                            <a
                              href={doc.signedUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="p-1.5 text-muted hover:text-blue-600 transition-colors flex-shrink-0"
                              title="Ver documento"
                            >
                              <OpenInNew fontSize="small" />
                            </a>
                          )}
                        </div>
                      ))}
                    </div>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </div>
        )}

        {/* Beneficial Owners */}
        {app.beneficialOwners.length > 0 && (
          <div className="mb-6">
            <h2 className="text-base font-semibold text-brand-white mb-3">Beneficiarios Finales</h2>
            <div className="space-y-1">
              {(() => {
                // Build the set of IDs that are nested children of a company UBO
                const childIds = new Set(
                  app.beneficialOwners.flatMap(u => (u.children ?? []).map(c => c.id))
                );
                return app.beneficialOwners
                  .filter(u => !u.parentId && !childIds.has(u.id))
                  .map(ubo => (
                  <Accordion
                    key={ubo.id}
                    disableGutters
                    elevation={0}
                    sx={{
                      backgroundColor: 'var(--color-surface)',
                      border: '1px solid var(--color-border)',
                      borderRadius: '8px !important',
                      '&:before': { display: 'none' },
                      '& .MuiAccordionSummary-expandIconWrapper': { color: 'var(--color-brand-white)' },
                    }}
                  >
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <div className="flex items-center gap-3">
                        <span className="font-medium text-brand-white text-sm">{ubo.name}</span>
                        <span className="text-xs text-muted">
                          {ubo.type === 'individual' ? 'Persona física' : 'Persona moral'}
                        </span>
                        <span className="text-xs text-muted">{ubo.ownershipPercentage}%</span>
                      </div>
                    </AccordionSummary>
                    <AccordionDetails>
                      <div className="space-y-4">
                        {/* Company's own documents */}
                        <UboDocList docs={ubo.documents ?? []} />

                        {/* Individual members of a company UBO */}
                        {ubo.type === 'company' && ubo.children && ubo.children.length > 0 && (
                          <div className="border-t border-border pt-4 space-y-4">
                            <p className="text-xs font-semibold text-muted uppercase tracking-wide">
                              Personas físicas miembros
                            </p>
                            {ubo.children.map(child => (
                              <div
                                key={child.id}
                                className="pl-3 border-l-2 border-border space-y-2"
                              >
                                <div className="flex items-center gap-2">
                                  <span className="text-sm font-medium text-brand-white">
                                    {child.name}
                                  </span>
                                  <span className="text-xs text-muted">
                                    {child.ownershipPercentage}% de {ubo.name}
                                  </span>
                                </div>
                                <UboDocList docs={child.documents ?? []} />
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </AccordionDetails>
                  </Accordion>
                  ));
              })()}
            </div>
          </div>
        )}
      </div>

      {/* Approve Confirmation Dialog */}
      <Dialog
        open={approveOpen}
        onClose={() => setApproveOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            boxShadow: '0 25px 50px rgba(0,0,0,0.6)',
          },
        }}
      >
        <DialogTitle sx={{ color: 'var(--color-brand-white)', borderBottom: '1px solid var(--color-border)' }}>
          Confirmar aprobación
        </DialogTitle>
        <DialogContent sx={{ pt: '16px !important' }}>
          <p className="text-sm text-muted">
            ¿Estás seguro de aprobar la solicitud de{' '}
            <strong className="text-brand-white">{app.companyName ?? app.userName}</strong>? Se creará la organización, las
            cuentas y se asignará el rol de administrador.
          </p>
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid var(--color-border)', p: 2, gap: 1 }}>
          <button
            onClick={() => setApproveOpen(false)}
            className="px-4 py-2 text-sm text-muted border border-border rounded-lg hover:bg-black/5 dark:hover:bg-white/5"
          >
            Cancelar
          </button>
          <button
            onClick={() => void handleApprove()}
            disabled={approveMutation.isPending}
            className="px-4 py-2 text-sm text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-60"
          >
            {approveMutation.isPending ? 'Aprobando...' : 'Aprobar'}
          </button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog
        open={rejectOpen}
        onClose={() => setRejectOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            boxShadow: '0 25px 50px rgba(0,0,0,0.6)',
          },
        }}
      >
        <DialogTitle sx={{ color: 'var(--color-brand-white)', borderBottom: '1px solid var(--color-border)' }}>
          Rechazar solicitud
        </DialogTitle>
        <DialogContent className="space-y-4" sx={{ pt: '16px !important' }}>
          <div>
            <label
              htmlFor="rejectStepOrder"
              className="block text-sm font-medium text-brand-white mb-1"
            >
              Paso a corregir
            </label>
            <Select
              value={rejectStepOrder}
              onChange={e => setRejectStepOrder(Number(e.target.value))}
              size="small"
              fullWidth
              sx={{
                color: 'var(--color-brand-white)',
                backgroundColor: 'var(--color-surface)',
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--color-border)' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--color-input-hover-border)' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--color-brand-blue)' },
                '& .MuiSvgIcon-root': { color: 'var(--color-muted)' },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    backgroundColor: 'var(--color-surface)',
                    border: '1px solid var(--color-border)',
                    '& .MuiMenuItem-root': { color: 'var(--color-brand-white)' },
                    '& .MuiMenuItem-root:hover': { backgroundColor: 'var(--color-hover-overlay)' },
                  },
                },
              }}
            >
              {Object.entries(STEP_TITLES).map(([order, title]) => (
                <MenuItem key={order} value={Number(order)}>
                  Paso {order}: {title}
                </MenuItem>
              ))}
            </Select>
          </div>
          <div>
            <label htmlFor="rejectMessage" className="block text-sm font-medium text-brand-white mb-1">
              Mensaje para el administrador *
            </label>
            <TextField
              multiline
              rows={4}
              fullWidth
              size="small"
              value={rejectMessage}
              onChange={e => setRejectMessage(e.target.value)}
              placeholder="Indica qué debe corregirse..."
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: 'var(--color-brand-white)',
                  backgroundColor: 'var(--color-surface)',
                  '& fieldset': { borderColor: 'var(--color-border)' },
                  '&:hover fieldset': { borderColor: 'var(--color-input-hover-border)' },
                  '&.Mui-focused fieldset': { borderColor: 'var(--color-brand-blue)' },
                },
                '& .MuiInputBase-input::placeholder': { color: 'var(--color-muted)', opacity: 1 },
              }}
            />
          </div>
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid var(--color-border)', p: 2, gap: 1 }}>
          <button
            onClick={() => setRejectOpen(false)}
            className="px-4 py-2 text-sm text-muted border border-border rounded-lg hover:bg-black/5 dark:hover:bg-white/5"
          >
            Cancelar
          </button>
          <button
            onClick={() => void handleReject()}
            disabled={rejectMutation.isPending}
            className="px-4 py-2 text-sm text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-60"
          >
            {rejectMutation.isPending ? 'Rechazando...' : 'Rechazar'}
          </button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
